<script>
import BaseSelect from '@/components/lavender/Select/BaseSelect.vue'
import { buildCountryCallingCodeOptions } from '@/utils/country-data'
import COUNTRY_NAMES from '@/constants/all-country-name'
import COUNTRY_CALLING_CODES from '@/constants/all-country-calling-code'

export default {
  name: 'LoginFormItemSelectCountry',
  components: { BaseSelect },
  props: {
    value: {
      type: [String, Number, Boolean, Object, Array],
      default: '',
    },
  },
  emits: ['input'],
  computed: {
    internalValue: {
      get() {
        return this.getOptionByCallingCode(this.value)?.value
      },
      set(val) {
        const selectedOption = this.getOptionByValue(val)
        const callingCode = selectedOption ? selectedOption.callingCode : ''

        this.$emit('input', callingCode)
      },
    },
    options() {
      return buildCountryCallingCodeOptions(
        COUNTRY_NAMES,
        COUNTRY_CALLING_CODES
      )
    },
    selectedOption() {
      return this.getOptionByCallingCode(this.value)
    },
  },
  methods: {
    getOptionByValue(val) {
      return this.options.find((option) => option.value === val)
    },
    getOptionByCallingCode(callingCode) {
      const code = callingCode.startsWith('+')
        ? callingCode.slice(1)
        : callingCode

      return this.options.find((option) => option.callingCode === code)
    },
  },
}
</script>

<template>
  <BaseSelect
    v-model="internalValue"
    class="login-form-item__select"
    popper-class="login-form-item__select__popper is-lavender-design"
  >
    <div slot="prefix" class="login-form-item__select__prefix">
      <flag :iso="selectedOption.isoCode" />
    </div>
    <el-option
      v-for="option in options"
      :key="option.key"
      :value="option.value"
    >
      <div class="login-form-item__select__option">
        <div class="login-form-item__select__option__country-flag">
          <flag :iso="option.isoCode" />
        </div>
        <div class="login-form-item__select__option__country-name">
          {{ option.name }}
        </div>
        <div class="login-form-item__select__option__country-calling-code">
          {{ option.label }}
        </div>
      </div>
    </el-option>
  </BaseSelect>
</template>

<style lang="scss" scoped>
@import '@/styles/lavender/design.scss';

.login-form-item__select {
  &__option {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;

    &__country-flag {
      display: flex;
      flex: 0 0 32px;
      align-items: center;

      ::v-deep {
        .fi.fis {
          width: 32px;
          font-size: 24px;
          line-height: 24px;
          background-size: cover;
        }
      }
    }

    &__country-name {
      flex: 1 1 auto;
      max-width: 240px;
      color: #202442;

      @extend .tamara-body-large;
    }

    &__country-calling-code {
      flex: 0 0 auto;
      margin-inline-start: 16px;
    }
  }
}
</style>

<style lang="scss">
@import '@/styles/mixins.scss';

.login-form-item__select {
  $itemHeight: 48px;
  $countryFlagWidth: 22px;
  $countryFlagHeight: 16px;
  $countryFlagSpacingStart: 14px;
  $arrowIconSpacingStart: 38px;

  .el-input__prefix {
    left: $countryFlagSpacingStart;
    display: flex;
    align-items: center;
    justify-content: center;
    width: $countryFlagWidth;
    height: 100%;

    .fi.fis {
      width: $countryFlagWidth;
      font-size: $countryFlagWidth;
      line-height: $countryFlagHeight;
      background-size: cover;
    }
  }

  .el-input__suffix {
    left: $arrowIconSpacingStart;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 100%;

    .el-select__caret {
      height: 100%;
      line-height: $itemHeight;
    }
  }

  .el-input__inner {
    padding-left: 60px !important;
  }

  &__prefix {
    width: $countryFlagWidth;
    height: $countryFlagHeight;
    overflow: hidden;
    border-radius: 2px;
  }

  &__popper {
    border: 1px solid #eee;
    border-radius: 12px;
    box-shadow: 0 3px 17px 0 #00000012;

    .el-select-dropdown__list {
      padding-top: 24px;
      padding-bottom: 24px;
    }

    .el-select-dropdown__item {
      height: 40px;
      padding-inline: 24px;

      .login-form-item__select__option {
        height: 40px;
        max-height: 100%;
      }
    }

    .el-select-dropdown__item.selected {
      font-weight: 500;
      color: #202442;
    }
  }
}

@include for-rtl {
  .login-form-item__select {
    &__popper {
      .el-scrollbar {
        overflow: hidden;
      }

      .el-select-dropdown__wrap {
        margin-left: -15px;
      }
    }
  }
}
</style>
